/**
 * Composant React pour les paramètres GDPR
 * Boss SEO Plugin
 */

import React, { useState, useEffect } from 'react';
import { __ } from '@wordpress/i18n';
import apiFetch from '@wordpress/api-fetch';
import { Button, Card, CardBody, CardHeader, TextControl, TextareaControl, ToggleControl, Notice, Spinner } from '@wordpress/components';

const GDPRSettings = () => {
    const [settings, setSettings] = useState({
        enabled: true,
        banner_title: '',
        banner_message: '',
        privacy_policy_url: '',
        anonymize_ip: true,
        data_retention_days: 365
    });
    const [consentStats, setConsentStats] = useState({
        total: 0,
        analytics: 0,
        marketing: 0
    });
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isTesting, setIsTesting] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [notices, setNotices] = useState([]);

    useEffect(() => {
        loadSettings();
        loadConsentStats();
    }, []);

    const loadSettings = async () => {
        try {
            const response = await apiFetch({
                path: '/boss-seo/v1/gdpr/settings'
            });
            if (response.success) {
                setSettings(response.data);
            }
        } catch (error) {
            
            addNotice(__('Erreur lors du chargement des paramètres', 'boss-seo'), 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const loadConsentStats = async () => {
        try {
            const response = await apiFetch({
                path: '/boss-seo/v1/gdpr/stats'
            });
            if (response.success) {
                setConsentStats(response.data);
            }
        } catch (error) {
            
        }
    };

    const saveSettings = async () => {
        setIsSaving(true);
        try {
            const response = await apiFetch({
                path: '/boss-seo/v1/gdpr/settings',
                method: 'POST',
                data: { settings }
            });

            if (response.success) {
                addNotice(__('Paramètres enregistrés avec succès', 'boss-seo'), 'success');
            } else {
                addNotice(response.data.message || __('Erreur lors de l\'enregistrement', 'boss-seo'), 'error');
            }
        } catch (error) {
            
            addNotice(__('Erreur lors de l\'enregistrement des paramètres', 'boss-seo'), 'error');
        } finally {
            setIsSaving(false);
        }
    };

    const testBanner = async () => {
        setIsTesting(true);
        try {
            const response = await apiFetch({
                path: '/boss-seo/v1/gdpr/test-banner',
                method: 'POST'
            });

            if (response.success) {
                addNotice(__('Test réussi. Visitez votre site pour voir la bannière.', 'boss-seo'), 'success');
                // Ouvrir le site dans un nouvel onglet
                window.open(response.data.preview_url, '_blank');
            } else {
                addNotice(response.data.message || __('Erreur lors du test', 'boss-seo'), 'error');
            }
        } catch (error) {
            
            addNotice(__('Erreur lors du test de la bannière', 'boss-seo'), 'error');
        } finally {
            setIsTesting(false);
        }
    };

    const exportConsentData = async () => {
        setIsExporting(true);
        try {
            const response = await apiFetch({
                path: '/boss-seo/v1/gdpr/export',
                method: 'POST'
            });

            if (response.success) {
                // Télécharger le fichier
                const link = document.createElement('a');
                link.href = response.data.download_url;
                link.download = response.data.filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                
                addNotice(__('Export terminé avec succès', 'boss-seo'), 'success');
            } else {
                addNotice(response.data.message || __('Erreur lors de l\'export', 'boss-seo'), 'error');
            }
        } catch (error) {
            
            addNotice(__('Erreur lors de l\'export des données', 'boss-seo'), 'error');
        } finally {
            setIsExporting(false);
        }
    };

    const addNotice = (message, type = 'info') => {
        const notice = {
            id: Date.now(),
            message,
            type
        };
        setNotices(prev => [...prev, notice]);
        
        // Supprimer automatiquement après 5 secondes
        setTimeout(() => {
            setNotices(prev => prev.filter(n => n.id !== notice.id));
        }, 5000);
    };

    const removeNotice = (id) => {
        setNotices(prev => prev.filter(n => n.id !== id));
    };

    const handleSettingChange = (key, value) => {
        setSettings(prev => ({
            ...prev,
            [key]: value
        }));
    };

    if (isLoading) {
        return (
            <div className="boss-seo-loading">
                <Spinner />
                <p>{__('Chargement des paramètres GDPR...', 'boss-seo')}</p>
            </div>
        );
    }

    return (
        <div className="boss-seo-gdpr-settings">
            <div className="boss-seo-admin-header">
                <h1>{__('Paramètres GDPR/Privacy - Boss SEO', 'boss-seo')}</h1>
                <p className="description">
                    {__('Configurez les paramètres de conformité GDPR pour respecter la réglementation européenne sur la protection des données.', 'boss-seo')}
                </p>
            </div>

            {/* Notifications */}
            {notices.map(notice => (
                <Notice
                    key={notice.id}
                    status={notice.type}
                    onRemove={() => removeNotice(notice.id)}
                    isDismissible
                >
                    {notice.message}
                </Notice>
            ))}

            <div className="boss-seo-admin-content">
                <div className="boss-seo-admin-main">
                    {/* Activation GDPR */}
                    <Card>
                        <CardHeader>
                            <h2>{__('Activation GDPR', 'boss-seo')}</h2>
                        </CardHeader>
                        <CardBody>
                            <ToggleControl
                                label={__('Activer GDPR', 'boss-seo')}
                                help={__('Active la gestion du consentement GDPR et les fonctionnalités de protection des données.', 'boss-seo')}
                                checked={settings.enabled}
                                onChange={(value) => handleSettingChange('enabled', value)}
                            />
                        </CardBody>
                    </Card>

                    {/* Configuration de la bannière */}
                    <Card>
                        <CardHeader>
                            <h2>{__('Configuration de la bannière', 'boss-seo')}</h2>
                        </CardHeader>
                        <CardBody>
                            <TextControl
                                label={__('Titre de la bannière', 'boss-seo')}
                                help={__('Le titre affiché dans la bannière de consentement.', 'boss-seo')}
                                value={settings.banner_title}
                                onChange={(value) => handleSettingChange('banner_title', value)}
                            />
                            
                            <TextareaControl
                                label={__('Message de la bannière', 'boss-seo')}
                                help={__('Le message explicatif affiché dans la bannière. HTML autorisé.', 'boss-seo')}
                                value={settings.banner_message}
                                onChange={(value) => handleSettingChange('banner_message', value)}
                                rows={4}
                            />
                            
                            <TextControl
                                label={__('URL Politique de confidentialité', 'boss-seo')}
                                help={__('Lien vers votre politique de confidentialité.', 'boss-seo')}
                                value={settings.privacy_policy_url}
                                onChange={(value) => handleSettingChange('privacy_policy_url', value)}
                                type="url"
                            />
                        </CardBody>
                    </Card>

                    {/* Paramètres de protection des données */}
                    <Card>
                        <CardHeader>
                            <h2>{__('Protection des données', 'boss-seo')}</h2>
                        </CardHeader>
                        <CardBody>
                            <ToggleControl
                                label={__('Anonymisation IP', 'boss-seo')}
                                help={__('Supprime le dernier octet des adresses IP pour respecter la vie privée.', 'boss-seo')}
                                checked={settings.anonymize_ip}
                                onChange={(value) => handleSettingChange('anonymize_ip', value)}
                            />
                            
                            <TextControl
                                label={__('Rétention des données (jours)', 'boss-seo')}
                                help={__('Durée de conservation des données de consentement (1-1095 jours).', 'boss-seo')}
                                value={settings.data_retention_days}
                                onChange={(value) => handleSettingChange('data_retention_days', parseInt(value) || 365)}
                                type="number"
                                min={1}
                                max={1095}
                            />
                        </CardBody>
                    </Card>

                    {/* Actions */}
                    <Card>
                        <CardBody>
                            <div className="boss-seo-form-actions">
                                <Button
                                    isPrimary
                                    isBusy={isSaving}
                                    disabled={isSaving}
                                    onClick={saveSettings}
                                >
                                    {isSaving ? __('Enregistrement...', 'boss-seo') : __('Enregistrer les paramètres', 'boss-seo')}
                                </Button>
                                
                                <Button
                                    isSecondary
                                    isBusy={isTesting}
                                    disabled={isTesting}
                                    onClick={testBanner}
                                >
                                    {isTesting ? __('Test en cours...', 'boss-seo') : __('Tester la bannière', 'boss-seo')}
                                </Button>
                            </div>
                        </CardBody>
                    </Card>
                </div>

                <div className="boss-seo-admin-sidebar">
                    {/* Statistiques de consentement */}
                    <Card>
                        <CardHeader>
                            <h3>{__('Statistiques de consentement', 'boss-seo')}</h3>
                        </CardHeader>
                        <CardBody>
                            <div className="boss-seo-stats">
                                <div className="stat-item">
                                    <span className="stat-number">{consentStats.total.toLocaleString()}</span>
                                    <span className="stat-label">{__('Total des consentements', 'boss-seo')}</span>
                                </div>
                                <div className="stat-item">
                                    <span className="stat-number">{consentStats.analytics.toLocaleString()}</span>
                                    <span className="stat-label">{__('Analytics acceptés', 'boss-seo')}</span>
                                </div>
                                <div className="stat-item">
                                    <span className="stat-number">{consentStats.marketing.toLocaleString()}</span>
                                    <span className="stat-label">{__('Marketing acceptés', 'boss-seo')}</span>
                                </div>
                            </div>
                            
                            <Button
                                isSecondary
                                isBusy={isExporting}
                                disabled={isExporting}
                                onClick={exportConsentData}
                                className="button-block"
                            >
                                {isExporting ? __('Export en cours...', 'boss-seo') : __('Exporter les données', 'boss-seo')}
                            </Button>
                        </CardBody>
                    </Card>

                    {/* Actions rapides */}
                    <Card>
                        <CardHeader>
                            <h3>{__('Actions rapides', 'boss-seo')}</h3>
                        </CardHeader>
                        <CardBody>
                            <div className="boss-seo-quick-actions">
                                <Button
                                    isSecondary
                                    href={`${window.ajaxurl.replace('admin-ajax.php', '')}tools.php?page=export_personal_data`}
                                    className="button-block"
                                >
                                    {__('Exporter données personnelles', 'boss-seo')}
                                </Button>
                                
                                <Button
                                    isSecondary
                                    href={`${window.ajaxurl.replace('admin-ajax.php', '')}tools.php?page=remove_personal_data`}
                                    className="button-block"
                                >
                                    {__('Supprimer données personnelles', 'boss-seo')}
                                </Button>
                                
                                {settings.privacy_policy_url && (
                                    <Button
                                        isSecondary
                                        href={settings.privacy_policy_url}
                                        target="_blank"
                                        className="button-block"
                                    >
                                        {__('Voir politique de confidentialité', 'boss-seo')}
                                    </Button>
                                )}
                            </div>
                        </CardBody>
                    </Card>

                    {/* Informations légales */}
                    <Card>
                        <CardHeader>
                            <h3>{__('Informations légales', 'boss-seo')}</h3>
                        </CardHeader>
                        <CardBody>
                            <div className="boss-seo-legal-info">
                                <p><strong>{__('RGPD (GDPR)', 'boss-seo')}</strong></p>
                                <p className="description">
                                    {__('Le Règlement Général sur la Protection des Données s\'applique à tous les sites traitant des données d\'utilisateurs européens.', 'boss-seo')}
                                </p>
                                
                                <p><strong>{__('Obligations', 'boss-seo')}</strong></p>
                                <ul className="description">
                                    <li>{__('Consentement explicite', 'boss-seo')}</li>
                                    <li>{__('Droit à l\'oubli', 'boss-seo')}</li>
                                    <li>{__('Portabilité des données', 'boss-seo')}</li>
                                    <li>{__('Notification des violations', 'boss-seo')}</li>
                                </ul>
                                
                                <p className="description">
                                    <em>{__('Consultez un avocat spécialisé pour une conformité complète.', 'boss-seo')}</em>
                                </p>
                            </div>
                        </CardBody>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default GDPRSettings;
